/**
 * Утилита для форматирования дат с поддержкой локализации
 * Использует библиотеку date-fns для форматирования
 */

import { format, parseISO } from 'date-fns';
import { bg, ru, enUS, de, es, fr, it, pl, ro, tr, uk, cs, el, hr, hu, mk, sr, sl, zhCN, faIR, be } from 'date-fns/locale';

// Карта локалей для date-fns
const localeMap = {
  bg: bg,
  ru: ru,
  en: enUS,
  de: de,
  es: es,
  fr: fr,
  it: it,
  pl: pl,
  ro: ro,
  tr: tr,
  uk: uk,
  cs: cs,
  el: el,
  hr: hr,
  hu: hu,
  mk: mk,
  sr: sr,
  sl: sl,
  be: be,
  zh: zhCN,
  fa: faIR,
};

// Карта форматов дат по локали
const formatMap = {
  // Европейские страны используют DD/MM/YYYY
  bg: 'dd/MM/yyyy',
  ru: 'dd/MM/yyyy',
  de: 'dd/MM/yyyy',
  es: 'dd/MM/yyyy',
  fr: 'dd/MM/yyyy',
  it: 'dd/MM/yyyy',
  pl: 'dd/MM/yyyy',
  ro: 'dd/MM/yyyy',
  uk: 'dd/MM/yyyy',
  cs: 'dd/MM/yyyy',
  el: 'dd/MM/yyyy',
  hr: 'dd/MM/yyyy',
  hu: 'dd/MM/yyyy',
  mk: 'dd/MM/yyyy',
  sr: 'dd/MM/yyyy',
  sl: 'dd/MM/yyyy',
  be: 'dd/MM/yyyy',

  // США и некоторые другие страны используют MM/DD/YYYY
  en: 'MM/dd/yyyy',

  // Турция использует DD.MM.YYYY
  tr: 'dd.MM.yyyy',

  // Китай
  zh: 'yyyy/MM/dd',

  // Персидский
  fa: 'dd/MM/yyyy',
};

/**
 * Получить локаль date-fns по коду языка
 * @param {string} langCode - код языка (например, 'bg', 'ru', 'en')
 * @returns {Locale} - локаль для date-fns
 */
export const getDateLocale = (langCode = 'en') => {
  return localeMap[langCode] || localeMap.en;
};

/**
 * Получить формат даты для локали
 * @param {string} langCode - код языка
 * @returns {string} - формат даты (например, 'dd/MM/yyyy')
 */
export const getDateFormat = (langCode = 'en') => {
  return formatMap[langCode] || formatMap.en;
};

/**
 * Форматировать дату для отображения
 * @param {string|Date} date - дата в формате ISO string или объект Date
 * @param {string} langCode - код языка
 * @param {string} customFormat - опциональный кастомный формат
 * @returns {string} - отформатированная дата
 */
export const formatDate = (date, langCode = 'en', customFormat = null) => {
  if (!date) return '';

  try {
    let dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (Number.isNaN(dateObj.getTime())) {
      // Fallback for loose input like "2024-3-5"
      dateObj = new Date(String(date).replace(/[./]/g, '-'));
      if (Number.isNaN(dateObj.getTime())) return '';
    }
    const dateFormat = customFormat || getDateFormat(langCode);
    const locale = getDateLocale(langCode);

    return format(dateObj, dateFormat, { locale });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Normalize many date representations into a YYYY-MM-DD string accepted by
 * native <input type="date">. Handles:
 *  - full ISO timestamps from Firestore/Timestamps (2024-03-15T10:00:00.000Z)
 *  - already-correct yyyy-MM-dd
 *  - Date objects
 *  - partial/manual input while typing (e.g. "2024-3-5") via Date fallback
 * Anything that cannot be parsed returns '' so the field shows empty rather
 * than throwing and wiping the month/day segment mid-entry.
 */
export const formatDateForInput = (date) => {
  if (!date) return '';

  // Already a Date object
  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) return '';
    return format(date, 'yyyy-MM-dd');
  }

  const str = String(date).trim();
  if (!str) return '';

  // Fast path: strict ISO yyyy-MM-dd or full timestamp → parseISO handles it.
  try {
    const parsed = parseISO(str);
    if (!Number.isNaN(parsed.getTime())) {
      return format(parsed, 'yyyy-MM-dd');
    }
  } catch (_) {
    /* fall through to manual parse */
  }

  // Fallback: tolerate loose manual input like "2024-3-5", "2024/3/5", etc.
  try {
    const loose = new Date(str.replace(/[./]/g, '-'));
    if (!Number.isNaN(loose.getTime())) {
      return format(loose, 'yyyy-MM-dd');
    }
  } catch (_) {
    /* ignore */
  }

  return '';
};

/**
 * Парсить дату из input type="date" (YYYY-MM-DD)
 * @param {string} dateString - дата в формате YYYY-MM-DD
 * @returns {Date|null} - объект Date или null
 */
export const parseDateFromInput = (dateString) => {
  if (!dateString) return null;
  
  try {
    return parseISO(dateString);
  } catch (error) {
    console.error('Error parsing date from input:', error);
    return null;
  }
};

/**
 * Получить пример формата даты для локали
 * @param {string} langCode - код языка
 * @returns {string} - пример формата (например, '31/12/2024')
 */
export const getDateFormatExample = (langCode = 'en') => {
  const exampleDate = new Date(2024, 11, 31); // 31 декабря 2024
  return formatDate(exampleDate, langCode);
};